import { useEffect, useState } from "react";
import { Download, Loader2, LogOut, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminCatalog } from "@/components/admin/AdminCatalog";
import { AdminCollection } from "@/components/admin/AdminCollection";
import { AdminCommunity } from "@/components/admin/AdminCommunity";
import { AdminNews } from "@/components/admin/AdminNews";

const API = process.env.REACT_APP_BACKEND_URL;
const HELP = { piezas: "Piezas", voluntariado: "Voluntariado", difusion: "Difusión", espacio: "Espacio", patrocinio: "Patrocinio", otro: "Otra idea" };

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : "Algo salió mal. Inténtalo de nuevo.");
const call = (path, opts = {}) => fetch(`${API}/api${path}`, { credentials: "include", ...opts });

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await call("/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) return setError(errText(data?.detail));
    onLogin(data);
  };
  return (
    <form onSubmit={submit} className="support-form mx-auto mt-10 max-w-md" data-testid="admin-login-form">
      <span className="grid h-11 w-11 place-items-center rounded-full border border-white/15"><Lock size={18} /></span>
      <h1 className="font-display mt-4 text-4xl">Acceso privado</h1>
      <p className="mt-2 text-sm text-[var(--muted)]">Apoyos, catálogo y colección.</p>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Correo" className="support-input mt-6" data-testid="admin-email-input" />
      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" className="support-input mt-3" data-testid="admin-password-input" />
      {error && <p className="mt-3 text-sm text-[#ff8a7a]" data-testid="admin-login-error">{error}</p>}
      <button type="submit" disabled={busy} className="btn-primary mt-6 w-full justify-center" data-testid="admin-login-btn">
        {busy && <Loader2 size={16} className="animate-spin" />} Entrar
      </button>
    </form>
  );
};

const Table = ({ rows }) => (
  <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10" data-lenis-prevent>
    <table className="w-full min-w-[760px] text-left text-sm" data-testid="supporters-table">
      <thead className="mono bg-white/5 text-[11px] uppercase tracking-[0.15em] text-[var(--muted)]">
        <tr>{["Fecha", "Nombre", "Correo", "Cómo ayuda", "Mensaje"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-white/10 align-top">
            <td className="whitespace-nowrap px-4 py-3 text-[var(--muted)]">{new Date(r.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}</td>
            <td className="px-4 py-3">{r.name}</td>
            <td className="px-4 py-3"><a href={`mailto:${r.email}`} className="underline decoration-white/30 underline-offset-4">{r.email}</a></td>
            <td className="px-4 py-3">
              <div className="flex flex-wrap gap-1">{(r.help_types ?? []).map((h) => <span key={h} className="rounded-full bg-[rgba(123,199,77,0.15)] px-2 py-0.5 text-xs text-[var(--green)]">{HELP[h] ?? h}</span>)}</div>
            </td>
            <td className="max-w-[320px] px-4 py-3 text-[var(--muted)]">{r.message || "—"}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--muted)]" data-testid="supporters-empty">Aún no hay registros.</td></tr>}
      </tbody>
    </table>
  </div>
);

const Supporters = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    call("/admin/supporters").then((r) => (r.ok ? r.json() : [])).then((d) => setRows(Array.isArray(d) ? d : []));
  }, []);
  const download = async () => {
    setBusy(true);
    const res = await call("/admin/supporters/export");
    setBusy(false);
    if (!res.ok) return;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = `apoyos-mutoni-${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="admin-supporters">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl">Quienes se han sumado</h2>
          <p className="mt-2 text-sm text-[var(--muted)]" data-testid="supporters-count">{rows ? `${rows.length} registro${rows.length === 1 ? "" : "s"}` : "Cargando…"}</p>
        </div>
        <button onClick={download} disabled={busy || !rows?.length} className="btn-primary disabled:opacity-50" data-testid="export-excel-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Descargar Excel
        </button>
      </div>
      {rows && <Table rows={rows} />}
    </div>
  );
};

const TABS = [["apoyos", "Apoyos", Supporters], ["catalogo", "Catálogo", AdminCatalog], ["coleccion", "Colección", AdminCollection], ["comunidad", "Comunidad", AdminCommunity], ["noticias", "Noticias", AdminNews]];

const Dashboard = ({ user, onLogout }) => (
  <div data-testid="admin-dashboard">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">Panel privado · {user.email}</p>
        <h1 className="font-display mt-3 text-4xl sm:text-5xl">La guarida</h1>
      </div>
      <button onClick={onLogout} className="btn-ghost" data-testid="admin-logout-btn"><LogOut size={16} /> Salir</button>
    </div>
    <Tabs defaultValue="apoyos" className="mt-8">
      <TabsList className="h-auto rounded-full border border-white/10 bg-white/5 p-1">
        {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-full px-5 py-2 data-[state=active]:bg-[var(--green)] data-[state=active]:text-[#0d0c0a]" data-testid={`admin-tab-${k}`}>{l}</TabsTrigger>)}
      </TabsList>
      {TABS.map(([k, , C]) => <TabsContent key={k} value={k} className="mt-8"><C /></TabsContent>)}
    </Tabs>
  </div>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    call("/auth/me").then((r) => (r.ok ? r.json() : false)).then(setUser).catch(() => setUser(false));
  }, []);
  const logout = async () => {
    await call("/auth/logout", { method: "POST" });
    setUser(false);
  };
  return (
    <main className="min-h-[80vh] px-5 pb-24 pt-32 md:px-10" data-testid="admin-page">
      <div className="mx-auto max-w-6xl">
        {user === null && <Loader2 className="mx-auto mt-20 animate-spin" />}
        {user === false && <Login onLogin={setUser} />}
        {user && <Dashboard user={user} onLogout={logout} />}
      </div>
    </main>
  );
}
